import { faker } from "@faker-js/faker";
import { IDirectoryDepartment, IDirectoryUser } from "./types";

// Create a fake department with a unique name
const createFakeDepartment = (usedNames: Set<string>): IDirectoryDepartment => {
  let name: string;

  // Generate a unique name
  do {
    name = faker.commerce.department();
  } while (usedNames.has(name));

  usedNames.add(name);

  return {
    id: faker.string.uuid(),
    name,
    parent_id: null, // Initially, no parent is assigned
    created_time: faker.date.past().getTime(),
  };
};

const createFakeDepartmentTree = (count: number): IDirectoryDepartment[] => {
  const usedNames = new Set<string>();
  const departments: IDirectoryDepartment[] = Array.from(
    { length: count },
    () => createFakeDepartment(usedNames)
  );

  // Ensure there is exactly one root node
  const root = faker.helpers.arrayElement(departments);

  // Assign parents to create a binary tree structure
  const queue: IDirectoryDepartment[] = [root]; // Use a queue to assign parents in a breadth-first manner
  let index = 0;

  departments.forEach((department) => {
    if (department.id !== root.id) {
      const parent = queue[index]; // Get the current parent from the queue

      // Assign the parent to the department
      department.parent_id = parent.id;

      // Add the department to the queue
      queue.push(department);

      // Move to the next parent if the current parent already has two children
      const childrenCount = departments.filter(
        (d) => d.parent_id === parent.id
      ).length;
      if (childrenCount === 2) {
        index++;
      }
    }
  });

  // Sort departments by path
  departments.sort((a, b) => {
    const pathA = `${a.name}`;
    const pathB = `${b.name}`;
    return pathA.localeCompare(pathB);
  });

  return departments;
};

// Generate fake departments
const FAKE_DEPARTMENTS = createFakeDepartmentTree(20);

const createFakeUser = () => {
  const department = faker.helpers.arrayElement(FAKE_DEPARTMENTS);
  return {
    id: faker.string.uuid(),
    email: faker.internet.email(),
    full_name: faker.person.fullName(),
    department,
    job_title: faker.person.jobTitle(),
    devices: [
      {
        id: faker.number.int({ min: 1, max: 100 }),
        name: faker.commerce.productName(),
      },
      {
        id: faker.number.int({ min: 1, max: 100 }),
        name: faker.commerce.productName(),
      },
      {
        id: faker.number.int({ min: 1, max: 100 }),
        name: faker.commerce.productName(),
      },
      {
        id: faker.number.int({ min: 1, max: 100 }),
        name: faker.commerce.productName(),
      },
      {
        id: faker.number.int({ min: 1, max: 100 }),
        name: faker.commerce.productName(),
      },
    ],
    vpn_enabled: faker.datatype.boolean(),
    created_time: faker.date.past().getTime(),
    updated_time: null,
  };
};

export const FAKE_DIRECTORY = {
  users: faker.helpers.multiple(createFakeUser, {
    count: 10,
  }) satisfies IDirectoryUser[],

  departments: FAKE_DEPARTMENTS,
};
